import React, { useState, useCallback } from "react";
import { useDropzone } from "react-dropzone";
import "./FileUploadContainer.css";
import Card from "../Card";
import { useTheme } from "../../context/ThemeContext";
import LoaderModal from "../LoaderModal";
import { MdOutlineFileUpload } from "react-icons/md";
import { MdInsertDriveFile } from "react-icons/md";
import { IoSettingsOutline } from "react-icons/io5";
import QuestionOptionModal from "./QuestionOptionModal";
import { generateQuestions } from "../../api/api";

const FileUploadContainer = ({ onQuestionSetGenerated }) => {
    const { themeStyles, theme } = useTheme();
    const [activeTab, setActiveTab] = useState("file"); // Active tab: 'file' or 'content'
    const [content, setContent] = useState("");
    const [file, setFile] = useState(null);
    const [numberOfQuestions, setNumberOfQuestions] = useState(10);
    const [difficulty, setDifficulty] = useState("medium");
    const [isLoading, setIsLoading] = useState(false);
    const [errorMessage, setErrorMessage] = useState(null); // File upload error message
    const [fileName, setFileName] = useState(""); // Display uploaded file name
    const [isOpen, setIsOpen] = useState(false);

    const onSubmit = (numberOfQuestions, difficulty) => {
        setNumberOfQuestions(numberOfQuestions);
        setDifficulty(difficulty);
        setIsOpen(false);
    }

    // Handle content changes
    const handleContentChange = (e) => {
        // if (e.target.value.split("\n").length <= 1000) {
        setContent(e.target.value);
        // }
    };

    // Handle drag-and-drop
    const onDrop = useCallback(
        (acceptedFiles, rejectedFiles) => {
            if (rejectedFiles.length > 0) {
                setErrorMessage("Unsupported file type. Please upload a valid PDF.");
                return;
            }

            setErrorMessage(null); // Clear previous errors
            const selectedFile = acceptedFiles[0];
            setFile(selectedFile);
            setFileName(selectedFile.name); // Show file name
        },
        []
    );

    const { getRootProps, getInputProps } = useDropzone({
        onDrop,
        accept: "application/pdf", // Only accept PDF files
        maxFiles: 1,
    });

    // Send API request to generate questions
    const sendRequest = async (data) => {
        try {
            const response = await generateQuestions(data);
            setIsLoading(false);
            onQuestionSetGenerated(response.questionSetId);
        } catch (error) {
            console.error("Error generating questions:", error);
            alert("The content is to big please decrease the content");
            setIsLoading(false);
        }
    };

    const handleGenerateQuestions = async () => {
        let formattedContent = content.replace(/\s+/g, ' ').trim();
        if (activeTab === "file" && !file) {
            alert("Please add file to generate questions")
            return
        }
        if (activeTab === "content" && formattedContent.length <= 500) {
            alert("Please add more content to generate questions")
            return;
        }

        setIsLoading(true);
        const requestData = { numberOfQuestions, difficulty };

        if (file && activeTab === "file") {
            const reader = new FileReader();
            const allowedTypes = [
                "application/pdf",
                "application/msword", // .doc
                "application/vnd.openxmlformats-officedocument.wordprocessingml.document", // .docx
                "text/plain" // .txt
            ];

            if (!allowedTypes.includes(file.type)) {
                console.error("Unsupported file type! Please upload a PDF, DOC, DOCX, or TXT file.");
                return;
            }

            reader.onloadend = () => {
                let fileData;

                if (file.type === "application/pdf") {
                    // Extract Base64 from Data URL for PDF
                    fileData = reader.result.split(",")[1];
                } else if (file.type === "text/plain") {
                    // Plain text file, no need for Base64 encoding
                    fileData = reader.result;
                } else {
                    // DOC and DOCX files are binary, so Base64 encoding is needed
                    fileData = reader.result.split(",")[1];
                }

                requestData.base64 = fileData;
                requestData.fileType = file.type;
                requestData.numberOfQuestions = numberOfQuestions;
                requestData.difficulty = difficulty;

                sendRequest(requestData);
            };

            if (file.type === "text/plain") {
                reader.readAsText(file); // Read TXT file as plain text
            } else {
                reader.readAsDataURL(file); // Read PDF, DOC, and DOCX as Base64
            }
        }


        if (formattedContent && activeTab === "content") {
            requestData.content = formattedContent;
            requestData.numberOfQuestions = numberOfQuestions;
            requestData.difficulty = difficulty;
            sendRequest(requestData);
        }
    };

    const characterCount = content.length;

    const removeUploadedFile = () => {
        setFile(null)
        setFileName("")
    }

    return (
        <div>
            {isLoading && <LoaderModal />}
            <div className="flex justify-center mb-6">
                <button
                    className={`px-6 py-2 w-150 text-lg ${activeTab === "file"
                        ? "bg-custom-blue text-white"
                        : "bg-white text-black border border-gray-400 shadow-sm"
                        } rounded-l`}
                    onClick={() => setActiveTab("file")}
                    style={{
                        backgroundColor: activeTab === "file" ? "#0052B4" : "#FFFFFF", // Active blue or white
                        color: activeTab === "file" ? "#FFFFFF" : "#000000", // White text for active, black for inactive
                    }}
                >
                    Upload File
                </button>
                <button
                    className={`px-6 py-2 w-150 text-lg ${activeTab === "content"
                        ? "bg-custom-blue text-white"
                        : "bg-white text-black border border-gray-400 shadow-sm"
                        } rounded-r`}
                    onClick={() => setActiveTab("content")}
                    style={{
                        backgroundColor: activeTab === "content" ? "#0052B4" : "#FFFFFF", // Active blue or white
                        color: activeTab === "content" ? "#FFFFFF" : "#000000", // White text for active, black for inactive
                    }}
                >
                    Upload Content
                </button>
            </div>

            <Card type="column">
                {/* File Upload Placeholder */}
                {activeTab === "file" && (
                    <div
                        style={{
                            // background: themeStyles.cardContentBackground,
                            border: `${file ? "2px solid" : "2px dashed"}  ${themeStyles.text}`,
                            background: theme === "dark" ? themeStyles.cardContentBackground : "#CDE3FF"
                        }}
                        className="card-content"
                        {...getRootProps()}
                    >

                        {file &&
                            <>
                                <MdInsertDriveFile size={100} color={theme === "dark" ? "#FFF" : themeStyles.primaryColor} />
                                <p style={{
                                    color: theme === "dark" ? "#FFF" : theme === "dark" ? "#FFF" : themeStyles.primaryColor,
                                    fontSize: "1.2rem", // Relative font size
                                    margin: "10px 0",
                                    textAlign: "center",
                                    fontWeight: 'bold'
                                }}>
                                    {fileName}
                                </p>
                                <button
                                    onClick={removeUploadedFile}
                                    className="file-type-circle"
                                    style={{
                                        width: "120px",
                                        height: "40px",
                                        marginTop: 10,
                                        borderRadius: 40,
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "center",
                                        backgroundColor: "#d8dee5",
                                        color: "#000",
                                        fontSize: "0.9rem", // Relative font size
                                    }}
                                >
                                    Change File
                                </button>
                            </>

                        }
                        {!file && <>
                            <input {...getInputProps()} />
                            <MdOutlineFileUpload size={100} color={theme === "dark" ? "#FFF" : themeStyles.primaryColor} />
                            <p
                                className="px-5 md:p-0"
                                style={{
                                    color: themeStyles.text,
                                    fontSize: "1.2rem", // Relative font size
                                    margin: "10px 0",
                                    textAlign: "center",
                                }}
                            >
                                Upload or drag and drop a file
                            </p>
                            {errorMessage && <p
                                style={{
                                    color: "red",
                                    fontSize: "1.2rem", // Relative font size
                                    margin: "10px 0",
                                    textAlign: "center",
                                }}
                            >
                                File Not supported please upload pdf, doc or txt.
                            </p>}
                            <div
                                className="file-types-container"
                                style={{
                                    display: "flex",
                                    justifyContent: "center",
                                    gap: "10px",
                                    marginTop: "10px",
                                }}
                            >
                                {[".pdf", ".doc", ".txt"].map((type) => (
                                    <div
                                        key={type}
                                        className="file-type-circle"
                                        style={{
                                            width: "60px",
                                            height: "40px",
                                            borderRadius: 40,
                                            display: "flex",
                                            alignItems: "center",
                                            justifyContent: "center",
                                            backgroundColor: "#d8dee5",
                                            color: "#424242",
                                            fontSize: "1rem", // Relative font size
                                        }}
                                    >
                                        {type}
                                    </div>
                                ))}
                            </div>
                        </>}
                    </div>
                )}



                {/* Content Input Section */}
                {activeTab === "content" && (
                    <div
                        style={{
                            background: themeStyles.cardContentBackground,
                            border: `1px solid ${themeStyles.text}`,
                            borderRadius: "8px",
                            padding: "10px",
                            overflowY: "auto",
                            position: "relative",
                        }}
                        className="card-content"
                    >
                        <textarea
                            value={content}
                            onChange={handleContentChange}
                            style={{
                                width: "100%",
                                height: "100%",
                                background: themeStyles.cardContentBackground,
                                color: themeStyles.text,
                                border: "none",
                                outline: "none",
                                resize: "none",
                                padding: "5px",
                                fontFamily: "Arial, sans-serif",
                                fontSize: "14px",
                            }}
                            placeholder="Type or paste your content here (up to 5000 characters)"
                        />
                        {/* Character Count */}
                        <div
                            style={{
                                position: "absolute",
                                bottom: "10px",
                                right: "10px",
                                fontSize: "12px",
                                color: themeStyles.text,
                            }}
                        >
                            {characterCount}/{5000}
                        </div>
                    </div>
                )}

                <div className="card-footer" style={{ display: "flex", justifyContent: "center", alignItems: "center", position: "relative", padding: 20, marginLeft: -20 }}>
                    {/* Settings Button */}
                    <button
                        onClick={() => setIsOpen(!isOpen)}
                        className="flex flex-row"
                    >
                        <IoSettingsOutline style={{ fontSize: 20 }} className="sidebar-icon" />
                        <span className="hidden ml-2 md:block">Settings</span>

                    </button>

                    {/* Generate Questions Button */}
                    <button
                        className="center bg-custom-blue font-bold p-2 rounded-md ml-5 text-white"
                        onClick={handleGenerateQuestions}
                    >
                        Generate Questions
                    </button>
                </div>

            </Card>
            <QuestionOptionModal isOpen={isOpen} onClose={() => setIsOpen(false)} onSubmit={onSubmit} numberOfQuestions={numberOfQuestions} difficulty={difficulty} />
        </div>
    );
};

export default FileUploadContainer;
